import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Paper,
  Alert,
  IconButton,
  Slider,
  Stack,
  Tooltip,
  Grid2,
  Divider,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import { observer } from "mobx-react";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { useStores } from "../../../use-stores";
import { Product } from "../../../../../models/Products/Product";
import { SortType } from "../../../../../models/SortType";
import { useLogoPlacement } from "./useLogoPlacement";
import { useMockupEditorPreviewUrl } from "./useMockupEditorPreviewUrl";
import type { PSDTemplate } from "../../../../../models/MockupConfig/index";
import PageTitle from "../../../../../views/components/common/pages/PageTitle";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import AppDropDown from "../../../../../views/components/common/filter/AppDropDown";
import AppSearchDropDown2 from "../../../../../views/components/common/filter/AppSearchDropdown2";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import ImageAPI from "../../../api/ImageAPI";

const DISPLAY_MAX_WIDTH = 600;
const DISPLAY_MAX_HEIGHT = 450;

const SECTION_PAPER_SX = {
  p: { xs: 2, sm: 3 },
  mb: 2.5,
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  boxShadow: "none",
  bgcolor: "background.paper",
} as const;

const OUTPUT_SIZE_BLURB: Record<"large" | "medium" | "small", string> = {
  large: "Full resolution",
  medium: "Web & sharing",
  small: "Compact / thumbs",
};

type MockupSize = "large" | "medium" | "small";

function pickPreferredMockupSize(): MockupSize {
  const conn = (navigator as any)?.connection;
  const effectiveType = conn?.effectiveType as string | undefined;
  // Typical values: 'slow-2g' | '2g' | '3g' | '4g'
  if (effectiveType === "4g") return "large";
  if (effectiveType === "3g") return "medium";
  if (effectiveType === "2g" || effectiveType === "slow-2g") return "small";
  return "medium";
}

function StepHeader({ step, title }: { step: number; title: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
      <Box
        aria-hidden
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.25,
          bgcolor: "primary.main",
          color: "primary.contrastText",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.875rem",
          fontWeight: 800,
          flexShrink: 0,
        }}
      >
        {step}
      </Box>
      <Typography
        variant="h6"
        component="h2"
        sx={{ fontWeight: 700, fontSize: "1.05rem", m: 0, lineHeight: 1.3 }}
      >
        {title}
      </Typography>
    </Stack>
  );
}

type MockupOutputUrls = {
  large: string;
  medium: string;
  small: string;
};

async function downloadMockupAsset(url: string, filename: string) {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(res.statusText);
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objUrl);
  } catch {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

function getProductId(product: Product | null): number | null {
  return product?.id ?? null;
}

function sortPsdTemplates(
  templates: PSDTemplate[],
  placementTypes: { id: number; sort_order?: number }[],
) {
  return [...templates].sort((a, b) => {
    const ptA = placementTypes.find(
      (pt) => pt.id === (a.placement_type_id ?? 0),
    );
    const ptB = placementTypes.find(
      (pt) => pt.id === (b.placement_type_id ?? 0),
    );
    const orderA = ptA?.sort_order ?? 99;
    const orderB = ptB?.sort_order ?? 99;
    if (orderA !== orderB) return orderA - orderB;
    return (a.view_angle ?? "").localeCompare(b.view_angle ?? "");
  });
}

const MockupConfig: React.FC = () => {
  const { showSuccess, showError } = useNotification();
  const { mockupConfigStore, productStore } = useStores();
  const imageAPI = new ImageAPI();
  const preferredSize = useMemo(() => pickPreferredMockupSize(), []);

  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [generatingMockup, setGeneratingMockup] = useState(false);
  const [mockupOutputs, setMockupOutputs] = useState<MockupOutputUrls | null>(
    null,
  );
  const [mockupReferenceId, setMockupReferenceId] = useState<string | null>(
    null,
  );
  const [selectedPsdForPreview, setSelectedPsdForPreview] = useState<
    number | null
  >(null);

  const sortedPsdTemplates = sortPsdTemplates(
    mockupConfigStore.psdTemplates,
    mockupConfigStore.placementTypes,
  );
  const selectedPsdTemplate = sortedPsdTemplates.find(
    (p) => p.id === selectedPsdForPreview,
  );

  const logoPlacement = useLogoPlacement({
    selectedPsdId: selectedPsdForPreview,
    psdTemplate: selectedPsdTemplate ?? null,
    onError: showError,
  });

  const handleBlankPreviewResolved = useCallback(
    (templateId: number, url: string) => {
      mockupConfigStore.setPsdTemplateBlankPreviewUrl(templateId, url);
    },
    [mockupConfigStore],
  );

  const { previewImageUrl, loadingPlacementPreview } = useMockupEditorPreviewUrl({
    template: selectedPsdTemplate,
    logoUrl: logoPlacement.logoUrl,
    onError: (title, message) => showError(title, message ?? ""),
    onBlankPreviewResolved: handleBlankPreviewResolved,
  });

  const loadProducts = useCallback(async () => {
    setLoadingProducts(true);
    productStore
      .getProducts({
        page_no: 1,
        limit: 30,
        sortBy: "name",
        sortType: SortType.ASC,
        name: "",
      })
      .catch((e) => showError("Error", e.message || "Failed to load products"))
      .finally(() => setLoadingProducts(false));
  }, [productStore]);

  useEffect(() => {
    mockupConfigStore
      .getPlacementTypes()
      .catch((e: unknown) =>
        showError(
          "Error",
          (e as Error)?.message || "Failed to load placement types",
        ),
      );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleProductSelect = useCallback(
    (product: Product | null) => {
      setSelectedProduct(product);
      const prId = getProductId(product);
      mockupConfigStore.setCatalogueId(prId);
      mockupConfigStore.resetProductData();
      if (prId) {
        mockupConfigStore
          .getPSDTemplates(prId)
          .catch((e: unknown) =>
            showError(
              "Error",
              (e as Error)?.message || "Failed to load PSD templates",
            ),
          );
      }
    },
    [mockupConfigStore],
  );

  useEffect(() => {
    setMockupOutputs(null);
    setMockupReferenceId(null);
  }, [selectedPsdForPreview]);

  const handleGenerateMockup = useCallback(async () => {
    const selectedLogoUrl = logoPlacement.getSelectedLogoUrl();
    if (!selectedLogoUrl) {
      showError("No logo", "Please upload and select a logo first.");
      return;
    }
    if (!selectedPsdForPreview || !selectedPsdTemplate) {
      showError("No template", "Please select a PSD template.");
      return;
    }

    const canvasW = selectedPsdTemplate.canvas_w ?? 800;
    const canvasH = selectedPsdTemplate.canvas_h ?? 600;
    const coords = logoPlacement.getCanvasCoords(canvasW, canvasH);
    if (!coords) {
      showError("Error", "Could not compute logo coordinates.");
      return;
    }

    setGeneratingMockup(true);
    try {
      const result = await mockupConfigStore.mockupConfigAPI.generateMockup({
        psd_template_id: selectedPsdForPreview,
        logo_url: selectedLogoUrl,
        x: coords.x,
        y: coords.y,
        width: coords.width,
        height: coords.height,
        rotation: coords.rotation,
      });
      setMockupOutputs(result.outputs ?? null);
      setMockupReferenceId(result.reference_id ?? null);
      showSuccess(
        result.message ||
        "Mockup generated — large, medium, and small are ready.",
      );
    } catch (e) {
      showError(
        "Generation failed",
        (e as Error)?.message || "Failed to generate mockup",
      );
    } finally {
      setGeneratingMockup(false);
    }
  }, [
    selectedPsdTemplate,
    selectedPsdForPreview,
    logoPlacement,
    mockupConfigStore,
    showError,
    showSuccess,
  ]);

  const handleCopyMockupUrl = useCallback(
    (url: string) => {
      void navigator.clipboard.writeText(url).then(
        () => showSuccess("Image link copied to clipboard."),
        () =>
          showError(
            "Copy failed",
            "Could not access the clipboard from this browser.",
          ),
      );
    },
    [showError, showSuccess],
  );

  const hasNoPsds =
    mockupConfigStore.catalogueId &&
    !mockupConfigStore.loadingPsd &&
    mockupConfigStore.psdTemplates.length === 0;

  const products = productStore.products;

  const productOptions: FilterValue[] = useMemo(
    () =>
      products.map((p) => ({
        id: String(getProductId(p) ?? p.id ?? ""),
        value: `${p.name || "Product"} (ID: ${getProductId(p) ?? p.id ?? "-"})`,
      })),
    [products],
  );

  const productValue: FilterValue[] = useMemo(
    () =>
      selectedProduct
        ? [
          {
            id: String(getProductId(selectedProduct)!),
            value: `${selectedProduct.name || "Product"} (ID: ${getProductId(selectedProduct)})`,
          },
        ]
        : [],
    [selectedProduct],
  );

  const handleProductDropdownSelect = useCallback(
    (items: FilterValue[]) => {
      const fv = items[0];
      if (!fv?.id) {
        handleProductSelect(null);
        return;
      }
      const p = products.find(
        (px) => String(getProductId(px) ?? px.id) === fv.id,
      );
      handleProductSelect(p ?? null);
    },
    [products, handleProductSelect],
  );

  const psdTemplateOptions: FilterValue[] = useMemo(() => {
    const base = sortedPsdTemplates
      .filter((p) => p.id != null)
      .map((psd) => ({
        id: String(psd.id!),
        value: psd.placement_type_name || `Template ${psd.id}`,
      }));
    return [{ id: "", value: "Choose a PSD view..." }, ...base];
  }, [sortedPsdTemplates]);

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        pb: 4,
        bgcolor: "grey.50",
        minHeight: "100%",
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: "auto" }}>
        <PageTitle title="Mockup Configuration" />
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5, mb: 2.5, maxWidth: 640 }}
        >
          Place artwork on PSD templates, adjust rotation, and export WebP
          mockups in three sizes.
        </Typography>

        <Paper sx={SECTION_PAPER_SX}>
          <StepHeader step={1} title="Select product" />
          <Box sx={{ maxWidth: { xs: "100%", sm: 520 } }}>
            <AppSearchDropDown2
              title="Product (from catalogue)"
              isMultiSelect={false}
              list={productOptions}
              value={productValue}
              local
              handleSelect={handleProductDropdownSelect}
              disabled={loadingProducts}
              minWidth="100%"
            />
          </Box>
        </Paper>

        {!mockupConfigStore.catalogueId && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            Select a product to configure mockup placements.
          </Alert>
        )}

        {mockupConfigStore.catalogueId && (
          <>
            {hasNoPsds && (
              <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                No PSD templates configured for this product. Add them first in
                admin/setup.
              </Alert>
            )}

            {!hasNoPsds && sortedPsdTemplates.length > 0 && (
              <Paper sx={SECTION_PAPER_SX}>
                <StepHeader step={2} title="Preview & generate" />
                <Stack spacing={2.5} sx={{ width: "100%" }}>
                  <Box>
                    <AppDropDown
                      title="PSD template / view"
                      isMultiSelect={false}
                      list={psdTemplateOptions}
                      value={
                        selectedPsdForPreview != null
                          ? String(selectedPsdForPreview)
                          : ""
                      }
                      onSingleSelectionChange={(id) =>
                        setSelectedPsdForPreview(id ? Number(id) : null)
                      }
                      placeholderText="Choose a PSD view..."
                      width="100%"
                      id="psd-template"
                    />
                  </Box>

                  {previewImageUrl && (
                    <Box
                      onDrop={logoPlacement.handleDrop}
                      onDragOver={logoPlacement.handleDragOver}
                    >
                      <AppButton
                        label="Upload logo — or drop on preview"
                        startIcon={<CloudUploadIcon />}
                        onClick={() => fileInputRef.current?.click()}
                      />
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".png,.jpg,.jpeg,.webp,.svg"
                        hidden
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          void imageAPI
                            .uploadFile(file, "mockup-logos")
                            .then((url) => {
                              if (!url) {
                                showError("Upload failed", "Failed to upload logo.");
                                return;
                              }
                              logoPlacement.handleLogoUrl(url);
                            })
                            .catch((err) => {
                              showError(
                                "Upload failed",
                                (err as Error)?.message || "Failed to upload logo.",
                              );
                            })
                            .finally(() => {
                              e.currentTarget.value = "";
                            });
                        }}
                      />
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {previewImageUrl && (
                      <Box
                        sx={{
                          borderRadius: 2,
                          p: 1,
                          bgcolor: "grey.100",
                          border: "1px solid",
                          borderColor: "divider",
                          boxShadow: "0 2px 12px rgba(15, 23, 42, 0.06)",
                          width: "fit-content",
                          maxWidth: "100%",
                          mx: "auto",
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: "block",
                            mb: 0.75,
                            fontWeight: 600,
                            letterSpacing: "0.02em",
                            textTransform: "uppercase",
                          }}
                        >
                          Canvas preview
                        </Typography>
                        <Box
                          ref={logoPlacement.previewRef}
                          onDrop={logoPlacement.handleDrop}
                          onDragOver={logoPlacement.handleDragOver}
                          sx={{
                            position: "relative",
                            width: DISPLAY_MAX_WIDTH,
                            maxWidth: "100%",
                            height: DISPLAY_MAX_HEIGHT,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            overflow: "hidden",
                            bgcolor: "common.white",
                          }}
                        >
                          <img
                            src={previewImageUrl}
                            alt="PSD Preview"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                              pointerEvents: "none",
                              opacity: loadingPlacementPreview ? 0.7 : 1,
                            }}
                            draggable={false}
                          />
                          {logoPlacement.logoUrl && (
                            <>
                              {logoPlacement.printAreaBounds ? (
                                <Box
                                  sx={{
                                    position: "absolute",
                                    left: logoPlacement.printAreaBounds.left,
                                    top: logoPlacement.printAreaBounds.top,
                                    width: logoPlacement.printAreaBounds.width,
                                    height:
                                      logoPlacement.printAreaBounds.height,
                                    overflow: "hidden",
                                    border: "1px dashed",
                                    borderColor: "primary.light",
                                    borderRadius: 0.5,
                                    zIndex: 9,
                                  }}
                                >
                                  <Box
                                    ref={logoPlacement.logoRef}
                                    onMouseDown={logoPlacement.handleDragStart}
                                    sx={{
                                      position: "absolute",
                                      left:
                                        logoPlacement.logoTransform.x -
                                        logoPlacement.printAreaBounds!.left,
                                      top:
                                        logoPlacement.logoTransform.y -
                                        logoPlacement.printAreaBounds!.top,
                                      width: logoPlacement.logoTransform.width,
                                      height:
                                        logoPlacement.logoTransform.height,
                                      cursor: logoPlacement.isDragging
                                        ? "grabbing"
                                        : "grab",
                                      border: "2px solid",
                                      borderColor: "primary.main",
                                      boxSizing: "border-box",
                                      transform: `rotate(${logoPlacement.logoTransform.rotation}deg)`,
                                      transformOrigin: "center center",
                                      zIndex: 10,
                                    }}
                                  >
                                    <img
                                      src={logoPlacement.logoUrl}
                                      alt="Logo"
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "contain",
                                        pointerEvents: "none",
                                      }}
                                      draggable={false}
                                    />
                                    <Box
                                      onMouseDown={
                                        logoPlacement.handleResizeStart
                                      }
                                      sx={{
                                        position: "absolute",
                                        right: -6,
                                        bottom: -6,
                                        width: 14,
                                        height: 14,
                                        backgroundColor: "primary.main",
                                        borderRadius: "50%",
                                        cursor: "nwse-resize",
                                        border: "2px solid white",
                                        zIndex: 11,
                                      }}
                                    />
                                  </Box>
                                </Box>
                              ) : (
                                <Box
                                  ref={logoPlacement.logoRef}
                                  onMouseDown={logoPlacement.handleDragStart}
                                  sx={{
                                    position: "absolute",
                                    left: logoPlacement.logoTransform.x,
                                    top: logoPlacement.logoTransform.y,
                                    width: logoPlacement.logoTransform.width,
                                    height: logoPlacement.logoTransform.height,
                                    cursor: logoPlacement.isDragging
                                      ? "grabbing"
                                      : "grab",
                                    border: "2px solid",
                                    borderColor: "primary.main",
                                    boxSizing: "border-box",
                                    transform: `rotate(${logoPlacement.logoTransform.rotation}deg)`,
                                    transformOrigin: "center center",
                                    zIndex: 10,
                                  }}
                                >
                                  <img
                                    src={logoPlacement.logoUrl}
                                    alt="Logo"
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "contain",
                                      pointerEvents: "none",
                                    }}
                                    draggable={false}
                                  />
                                  <Box
                                    onMouseDown={
                                      logoPlacement.handleResizeStart
                                    }
                                    sx={{
                                      position: "absolute",
                                      right: -6,
                                      bottom: -6,
                                      width: 14,
                                      height: 14,
                                      backgroundColor: "primary.main",
                                      borderRadius: "50%",
                                      cursor: "nwse-resize",
                                      border: "2px solid white",
                                      zIndex: 11,
                                    }}
                                  />
                                </Box>
                              )}
                            </>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>

                  {previewImageUrl && logoPlacement.logoUrl && (
                    <Box
                      sx={{
                        maxWidth: DISPLAY_MAX_WIDTH,
                        width: "100%",
                        mx: "auto",
                      }}
                    >
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          borderColor: "divider",
                          bgcolor: "grey.50",
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.06em",
                          }}
                        >
                          Rotation
                        </Typography>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ mt: 1 }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => logoPlacement.rotate(-15)}
                            aria-label="Rotate 15 degrees counter-clockwise"
                          >
                            <RotateLeftIcon fontSize="small" />
                          </IconButton>
                          <Slider
                            size="small"
                            value={logoPlacement.logoTransform.rotation}
                            min={-180}
                            max={180}
                            onChange={(_, v) =>
                              logoPlacement.setRotation(v as number)
                            }
                            valueLabelDisplay="auto"
                            sx={{ flex: 1 }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => logoPlacement.rotate(15)}
                            aria-label="Rotate 15 degrees clockwise"
                          >
                            <RotateRightIcon fontSize="small" />
                          </IconButton>
                          <Typography
                            variant="caption"
                            sx={{
                              minWidth: 36,
                              textAlign: "right",
                              fontVariantNumeric: "tabular-nums",
                            }}
                          >
                            {logoPlacement.logoTransform.rotation}°
                          </Typography>
                        </Stack>
                      </Paper>
                    </Box>
                  )}

                  {previewImageUrl && (
                    <Box
                      sx={{
                        maxWidth: DISPLAY_MAX_WIDTH,
                        width: "100%",
                        mx: "auto",
                      }}
                    >
                      <AppButton
                        label={
                          generatingMockup ? "Generating…" : "Generate mockup"
                        }
                        variant="primary"
                        onClick={handleGenerateMockup}
                        disabled={!logoPlacement.logoUrl || generatingMockup}
                        customStyles={{ width: "100%" }}
                      />
                    </Box>
                  )}

                  {previewImageUrl && mockupOutputs && (
                    <Box sx={{ width: "100%", pt: 0.5 }}>
                      <Divider sx={{ mb: 2 }} />
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, mb: 1.5 }}
                      >
                        Exports
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 2 }}
                      >
                        Recommended size based on your network
                      </Typography>
                      {mockupOutputs[preferredSize] && (
                        <Grid2 container spacing={2}>
                          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                            <Paper
                              variant="outlined"
                              sx={{
                                height: "100%",
                                borderRadius: 2,
                                overflow: "hidden",
                                display: "flex",
                                flexDirection: "column",
                                borderColor: "divider",
                              }}
                            >
                              <Box
                                sx={{
                                  aspectRatio: "1",
                                  bgcolor: "grey.100",
                                  p: 1,
                                }}
                              >
                                <Box
                                  component="img"
                                  src={mockupOutputs[preferredSize]}
                                  alt={`${preferredSize} mockup preview`}
                                  sx={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                  }}
                                />
                              </Box>
                              <Box
                                sx={{
                                  p: 1.5,
                                  flex: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                  bgcolor: "background.paper",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  fontWeight={700}
                                  textTransform="capitalize"
                                >
                                  {preferredSize}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {OUTPUT_SIZE_BLURB[preferredSize]}
                                </Typography>
                                <Stack
                                  direction="row"
                                  spacing={0.25}
                                  sx={{ mt: "auto", pt: 1.25 }}
                                >
                                  <Tooltip title="Download">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() =>
                                        downloadMockupAsset(
                                          mockupOutputs[preferredSize],
                                          `mockup-${mockupReferenceId ?? "export"}-${preferredSize}.webp`,
                                        )
                                      }
                                      aria-label={`Download ${preferredSize} mockup`}
                                    >
                                      <DownloadIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Copy image URL">
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleCopyMockupUrl(
                                          mockupOutputs[preferredSize],
                                        )
                                      }
                                      aria-label={`Copy ${preferredSize} URL`}
                                    >
                                      <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Open in new tab">
                                    <IconButton
                                      size="small"
                                      component="a"
                                      href={mockupOutputs[preferredSize]}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      aria-label={`Open ${preferredSize} in new tab`}
                                    >
                                      <OpenInNewIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              </Box>
                            </Paper>
                          </Grid2>
                        </Grid2>
                      )}
                    </Box>
                  )}

                  {previewImageUrl && (
                    <Typography variant="caption" color="text.secondary">
                      Logo is composited on the template; outputs are saved as
                      WebP on S3.
                    </Typography>
                  )}
                </Stack>

                {selectedPsdForPreview && !previewImageUrl && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    No preview URL for this PSD template (add preview_url or
                    psd_url in setup)
                  </Alert>
                )}
              </Paper>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default observer(MockupConfig);
